/**
 * Moves password-recovery bounces off the marketing homepage.
 *
 * Supabase's /auth/v1/verify redirects to SITE_URL — the homepage — whenever
 * the recovery token is already spent/expired, or when its `redirect_to` isn't
 * in the project's allow-list. Tenants therefore landed on
 * `https://…/#error=access_denied&error_code=otp_expired&error_description=…`
 * with the marketing page rendering as if nothing had happened.
 *
 * send-password-reset now links straight to /reset-password with a token hash,
 * so new emails never take that detour — but links already sitting in inboxes
 * still do. This forwards them (hash intact) to the page that can actually
 * explain the failure and offer a fresh link.
 *
 * Scoped to "/" on purpose: that's the only route SITE_URL fallbacks land on,
 * so no other flow's hash can be hijacked.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function RecoveryLinkRedirect() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname !== "/") return;

    // Insurance: a caller that passes a bare origin as `redirectTo` would drop
    // the token_hash on the homepage, where nothing redeems it. Checked before
    // the hash guard below — this variant carries no hash at all.
    const query = new URLSearchParams(window.location.search);
    if (
      query.get("token_hash") &&
      (query.get("type") ?? "recovery") === "recovery"
    ) {
      navigate(`/reset-password${window.location.search}`, { replace: true });
      return;
    }

    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const params = new URLSearchParams(hash);

    // Recovery tokens that bounced to the wrong route: /reset-password can
    // still redeem them.
    const hasRecoveryTokens =
      params.get("type") === "recovery" && Boolean(params.get("access_token"));
    // An error bounce carries no `type`, so an expired invite is
    // indistinguishable from an expired reset. /reset-password is the right
    // landing for both: it names the expiry and links to "request a new link".
    const hasAuthError = Boolean(
      params.get("error_code") || params.get("error")
    );
    if (!hasRecoveryTokens && !hasAuthError) return;

    navigate(`/reset-password${window.location.hash}`, { replace: true });
  }, [pathname, navigate]);

  return null;
}
